import { z } from 'zod'

export const modelBackendEnvelope = z.object({
  data: z.unknown().nullable(),
  errors: z.record(z.string(), z.array(z.string())).default({}),
  message: z.string().nullable().optional(),
  status: z.string(),
  meta: z.record(z.string(), z.unknown()).default({}),
})

export type ModelBackendEnvelope = z.infer<typeof modelBackendEnvelope>

export const modelCardTemplateId = z.string().min(1)
export type ModelCardTemplateId = z.infer<typeof modelCardTemplateId>

const modelTemplateAmount = z.string().regex(/^\d+(\.\d+)?$/)
export const modelCardTemplate = z.object({
  id: modelCardTemplateId,
  title: z.string().min(1),
  currency: z.string(),
  issuePrice: modelTemplateAmount,
  issueCurrency: z.string(),
  transactionFee: modelTemplateAmount,
  providerId: z.string().optional(),
  requiresKyc: z.boolean(),
  topup: z.object({ minAmount: modelTemplateAmount, maxAmount: modelTemplateAmount }),
  withdraw: z.object({ minAmount: modelTemplateAmount, maxAmount: modelTemplateAmount }),
  rates: z.object({
    topupRate: modelTemplateAmount,
    withdrawRate: modelTemplateAmount,
    validTime: z.number().int().positive(),
    updatedAt: z.number().int().nonnegative(),
  }),
})
export type ModelCardTemplate = z.infer<typeof modelCardTemplate>
export const modelCardTemplatesResponse = modelBackendEnvelope.extend({ data: z.array(modelCardTemplate).nullable() })

export const modelUserMeResponse = modelBackendEnvelope.extend({
  data: z.object({
    id: z.number(),
    createdAt: z.number(),
    phone: z.object({ number: z.string(), verified: z.boolean() }).nullable(),
    email: z.object({ address: z.string().email(), verified: z.boolean() }).nullable().default(null),
    telegram: z.object({ id: z.string(), username: z.string().nullable() }).nullable(),
  }),
})

export type ModelUserMe = z.infer<typeof modelUserMeResponse>['data']

const modelPaymentState = z.preprocess(
  (state) => (state === 'canceled' || state === 'cancelled' ? 'failed' : state),
  z.enum(['pending', 'processing', 'completed', 'failed', 'expired']),
)

export const modelCardIssueInvoice = z.object({
  createdAt: z.number().optional(),
  txId: z.string().min(1),
  cardId: z.string().min(1),
  templateId: modelCardTemplateId,
  amount: z.string(),
  currency: z.string(),
  usdAmount: z.string().nullable(),
  rate: z.string().nullable(),
  qrUrl: z.string().url().nullable(),
  paymentLink: z.string().nullable(),
  payeePhone: z.string().nullable(),
  state: modelPaymentState,
  expiresAt: z.number().nullable(),
})
export type ModelCardIssueInvoice = z.infer<typeof modelCardIssueInvoice>
export function isCardIssuePaymentActive(invoice: ModelCardIssueInvoice | null): boolean {
  return Boolean(
    invoice &&
      (invoice.state === 'pending' || invoice.state === 'processing') &&
      (invoice.expiresAt === null || invoice.expiresAt * 1000 > Date.now()),
  )
}
export type CardIssueState = { invoice: ModelCardIssueInvoice | null; isLoading: boolean; error: string | null }
export const modelCardIssueResponse = modelBackendEnvelope.extend({ data: modelCardIssueInvoice.nullable() })
export const modelSbpStateResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      txId: z.string(),
      operation: z.string(),
      cardId: z.string(),
      amount: z.string(),
      currency: z.string(),
      state: modelPaymentState,
      qrUrl: z.string().nullable(),
      expiresAt: z.number().int().nullable(),
      paidAt: z.number().int().nullable(),
      failureCode: z.string().nullable(),
    })
    .nullable(),
})

export const modelCardTopupInvoice = modelCardIssueInvoice.omit({ templateId: true, createdAt: true }).extend({
  templateId: modelCardTemplateId.nullable(),
  usdAmount: z.string(),
  rate: z.string(),
})
export type ModelCardTopupInvoice = z.infer<typeof modelCardTopupInvoice>
export const modelCardTopupResponse = modelBackendEnvelope.extend({ data: modelCardTopupInvoice.nullable() })

export const modelEmailCreateResponse = modelBackendEnvelope.extend({
  data: z.object({ idempotency: z.string(), retryAfter: z.number() }).nullable(),
})
export const modelEmailVerifyResponse = modelBackendEnvelope.extend({
  data: z.object({ email: z.object({ address: z.string().email(), verified: z.boolean() }) }).nullable(),
})

export const WithdrawBankId = {
  Sber: 'sber',
  Tbank: 'tbank',
  Alfa: 'alfa',
  Vtb: 'vtb',
  Yandex: 'yandex',
  Domrf: 'domrf',
  Gazprom: 'gazprom',
  KgBakai: 'kg-bakai',
  KgDemir: 'kg-demir',
  KgKicb: 'kg-kicb',
  KgMbank: 'kg-mbank',
  KgOptima: 'kg-optima',
  Mts: 'mts',
  Ozon: 'ozon',
  Post: 'post',
  Raiffeisen: 'raiffeisen',
  Rocket: 'rocket',
  Rshb: 'rshb',
  Sovcom: 'sovcom',
  Spb: 'spb',
  Wb: 'wb',
} as const

// Справочник приходит с сервера: новые идентификаторы не должны ломать загрузку банков.
export const modelWithdrawBankId = z.string().min(1)
export type WithdrawBankId = z.infer<typeof modelWithdrawBankId>

// Карта проверяется на границе API; экраны получают уже разобранные поля.
// Курсы конкретной карты приходят в ответе карт: они актуальны даже если шаблон отключили или изменили.
export const modelCardRates = z.object({
  topupRate: modelTemplateAmount,
  withdrawRate: modelTemplateAmount,
  validTime: z.number().int().positive(),
  updatedAt: z.number().int().nonnegative(),
})
export type ModelCardRates = z.infer<typeof modelCardRates>

export const modelCard = z.object({
  id: z.string(),
  templateId: modelCardTemplateId,
  name: z.string().nullable().optional(),
  status: z.enum([
    'pending',
    'active',
    'freezing',
    'frozen',
    'unfreezing',
    'blocking',
    'blocked',
    'processing',
    'failed',
  ]),
  balance: z.string(),
  currency: z.string(),
  // До завершения выпуска номер карты отсутствует; в UI используем пустую строку.
  maskedPan: z
    .string()
    .nullable()
    .transform((value) => value ?? ''),
  billingAddress: z
    .object({
      country: z.string(),
      address: z.string(),
      city: z.string(),
      region: z.string().nullable().optional(),
      postalCode: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.number(),
  type: modelCardTemplateId.optional(),
  rates: modelCardRates.nullable().optional(),
})
export type ModelBackendCard = z.infer<typeof modelCard>
export const modelCardsResponse = modelBackendEnvelope.extend({ data: z.array(modelCard).nullable() })

export const modelCardPinResponse = modelBackendEnvelope.extend({
  data: z
    .discriminatedUnion('mode', [
      z.object({ mode: z.literal(1), url: z.null() }),
      z.object({ mode: z.literal(2), url: z.string().url() }),
    ])
    .nullable(),
})
export const modelCardSetPinResponse = modelBackendEnvelope.extend({ data: z.null() })

export const modelCardSensitive = z.object({
  number: z.string(),
  cvv: z.string(),
  expiryDate: z.string().nullable(),
  holder: z.string().nullable(),
  templateId: modelCardTemplateId,
  status: z.string(),
})
export type ModelBackendCardSensitive = z.infer<typeof modelCardSensitive>
export const modelCardSensitiveResponse = modelBackendEnvelope.extend({ data: modelCardSensitive.nullable() })

export const modelBank = z.object({
  id: modelWithdrawBankId,
  name: z.string(),
  country: z.string(),
  bankId: z.string().nullable(),
  logoUrl: z.string().nullable(),
  color: z.string().nullable(),
  primary: z.boolean(),
  deeplinkScheme: z.string().nullable(),
})
export type ModelBackendBank = z.infer<typeof modelBank>
export const modelBanksResponse = modelBackendEnvelope.extend({ data: z.array(modelBank).nullable() })

export const modelTransaction = z.object({
  txHash: z.string(),
  templateId: modelCardTemplateId.nullish(),
  txId: z.string().optional(),
  description: z.string().nullable().optional(),
  refundNote: z.string().nullable().optional(),
  qrUrl: z.string().nullable().optional(),
  type: z.enum(['cardIssue', 'topup', 'withdraw', 'purchase', 'purchaseReversal']),
  createdAt: z.number(),
  status: z.enum(['pending', 'processing', 'completed', 'failed', 'canceled', 'expired']),
  direction: z.enum(['in', 'out']),
  value: z.string(),
  currency: z.string(),
  billingValue: z.string().nullable().optional(),
  billingCurrency: z.string().nullable().optional(),
  exchangeRate: z.string().nullable().optional(),
  fee: z.string().nullable().optional(),
  shopName: z.string().nullable().optional(),
  mcc: z.string().nullable().optional(),
  cardId: z.string().nullable().optional(),
  last4: z.string().nullable().optional(),
  bankId: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  totalValue: z.string().nullable(),
  paymentSystem: z.string().nullable(),
  expiresAt: z.number().int().nullable(),
  failureCode: z.string().nullable(),
  linkedTxHash: z.string().nullable().optional(),
  completedAt: z.number().nullable().optional(),
})
export type ModelBackendTransaction = z.infer<typeof modelTransaction>
export const modelTransactionsResponse = modelBackendEnvelope.extend({
  data: z.array(modelTransaction).nullable(),
  meta: z.object({
    currentPage: z.number().int(),
    nextPage: z.number().int().nullable(),
    totalPages: z.number().int(),
    totalEntries: z.number().int(),
  }),
})

export const modelCardResponse = modelBackendEnvelope.extend({ data: modelCard.nullable() })

const modelPreparation = z.object({
  idempotency: z.string().min(1),
  validTime: z.number().int().positive(),
  updatedAt: z.number().int(),
})
export const modelCardIssuePrepareResponse = modelBackendEnvelope.extend({
  data: modelPreparation
    .extend({
      templateId: modelCardTemplateId,
      issuePrice: z.string(),
      issueCurrency: z.string(),
    })
    .nullable(),
})
export const modelCardTopupPrepareResponse = modelBackendEnvelope.extend({
  data: modelPreparation
    .extend({
      cardId: z.string(),
      amount: z.string(),
      currency: z.string(),
      rate: z.string(),
      usdAmount: z.string(),
      fee: z.string(),
      minAmount: z.string(),
      maxAmount: z.string(),
    })
    .nullable(),
})
export const modelCardWithdrawPrepareResponse = modelBackendEnvelope.extend({
  data: modelPreparation
    .extend({
      cardId: z.string(),
      amount: z.string(),
      currency: z.string(),
      rate: z.string(),
      receiveAmount: z.string(),
      receiveCurrency: z.string(),
      fee: z.string(),
      bankId: z.string(),
      bankName: z.string(),
      phone: z.string(),
      minAmount: z.string(),
      maxAmount: z.string(),
    })
    .nullable(),
})
export const modelPhoneCreateResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      idempotency: z.string().min(1),
      source: z.string().nullable(),
      retryAfter: z.number().nonnegative().default(60),
    })
    .nullable(),
})
export const modelPhoneRetryResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      idempotency: z.string().min(1),
      source: z.string().nullable(),
      retriesLeft: z.number().int(),
      retryAfter: z.number().nonnegative().default(60),
    })
    .nullable(),
})
export const modelCardBlockPrepareResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      idempotency: z.string().min(1),
      validTime: z.number().int().positive(),
      channel: z.string(),
    })
    .nullable(),
})
export const modelPhoneVerifyResponse = modelBackendEnvelope.extend({
  data: z.object({ verified: z.boolean(), source: z.string().nullable() }).nullable(),
})
export const modelTransactionResponse = modelBackendEnvelope.extend({ data: modelTransaction.nullable() })

export const modelCardWithdrawResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      txId: z.string().min(1),
      cardId: z.string().min(1),
      amount: z.string(),
      currency: z.string(),
      receiveAmount: z.string(),
      receiveCurrency: z.string(),
      bankId: z.string(),
      bankName: z.string(),
      phone: z.string(),
      state: modelPaymentState,
      createdAt: z.number(),
    })
    .nullable(),
})
export const modelTelegramLinkingTokenResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      linkingToken: z.string().min(1),
      botName: z.string().min(1),
      expiredAt: z.number(),
      status: z.literal('pending'),
    })
    .nullable(),
})
export const modelTelegramConfirmLinkResponse = modelBackendEnvelope.extend({
  data: z
    .object({
      status: z.literal('confirmed'),
      telegramId: z.string(),
      telegramUsername: z.string().nullable(),
    })
    .nullable(),
})

// Совместимость публичных типов с существующими потребителями фичи.
export type BackendEnvelope = ModelBackendEnvelope
export type CardTemplateId = ModelCardTemplateId
export type CardTemplate = ModelCardTemplate
export type UserMe = ModelUserMe
export type CardIssueInvoice = ModelCardIssueInvoice
export type CardTopupInvoice = ModelCardTopupInvoice
export type BackendCardSensitive = ModelBackendCardSensitive
export type BackendCard = ModelBackendCard
export type BackendBank = ModelBackendBank
export type BackendTransaction = ModelBackendTransaction
